import { useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  MagnifyingGlass,
  X,
  Funnel,
  FunnelSimple,
} from "@phosphor-icons/react";

/**
 * Excel-style column filter popover.
 *
 * Props:
 *   - values        : full unique-value list for this column (array of {value, label?})
 *   - selected      : Set<string> of values currently checked (null/empty = "all")
 *   - sortDir       : 'asc' | 'desc' | null
 *   - onChange      : ({selected, sortDir}) => void
 *   - onClose       : () => void  optional; caller can hide the popover
 *   - column        : optional column key for data-testid ('date','description','category','amount')
 *   - anchor        : optional {top, left} for absolute-positioning fallback
 *   - align         : 'left' | 'right' (of trigger)
 *
 * Renders NOTHING as trigger — the caller renders the header cell + funnel button
 * and manages `open` state; this component is the popover panel only.
 */
export default function ColumnFilter({
  values = [],
  selected,
  sortDir,
  onChange,
  onClose,
  column = "col",
  align = "left",
}) {
  const [search, setSearch] = useState("");
  const [localSelected, setLocalSelected] = useState(() => new Set(selected || []));
  const [localSort, setLocalSort] = useState(sortDir || null);
  const rootRef = useRef(null);

  // Sync when parent updates
  useEffect(() => {
    setLocalSelected(new Set(selected || []));
  }, [selected]);
  useEffect(() => setLocalSort(sortDir || null), [sortDir]);

  // Click outside dismisses
  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);

  const filtered = search.trim()
    ? values.filter((v) =>
        String(v.label ?? v.value).toLowerCase().includes(search.trim().toLowerCase()),
      )
    : values;

  const isNone = localSelected.has("__NONE__");
  const allChecked = !isNone && (localSelected.size === 0 || localSelected.size === values.length);

  const toggle = (v) => {
    setLocalSelected((prev) => {
      const wasNone = prev.has("__NONE__");
      const next = new Set(prev);
      next.delete("__NONE__");    // drop sentinel — actual subset state below
      // If we were in the "all-checked" state (empty set), pre-fill so the
      // toggle acts as "uncheck one from all".
      if (!wasNone && next.size === 0) {
        for (const x of values) next.add(String(x.value));
      }
      const s = String(v);
      if (next.has(s)) next.delete(s); else next.add(s);
      // Collapse back to "all" when everything is checked again
      if (next.size === values.length && values.length > 0) return new Set();
      // Collapse to "none" when subset becomes empty
      if (next.size === 0) return new Set(["__NONE__"]);
      return next;
    });
  };

  const selectAll = () => setLocalSelected(new Set());          // empty = all
  const clearAll  = () => setLocalSelected(new Set(["__NONE__"])); // sentinel = none

  const apply = () => {
    onChange?.({ selected: localSelected, sortDir: localSort });
    onClose?.();
  };

  const clearFilter = () => {
    onChange?.({ selected: new Set(), sortDir: null });
    onClose?.();
  };

  return (
    <div
      ref={rootRef}
      data-testid={`column-filter-${column}`}
      className={`absolute z-40 mt-1 w-64 rounded-xl border border-white/10 bg-[#12131C] shadow-2xl p-3 ${
        align === "right" ? "right-0" : "left-0"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Sort */}
      <div className="mb-2">
        <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1">
          Ordenar
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid={`sort-asc-${column}`}
            onClick={() => setLocalSort(localSort === "asc" ? null : "asc")}
            className={`flex-1 px-2 py-1.5 rounded text-[11px] font-mono uppercase tracking-widest font-bold transition flex items-center justify-center gap-1 ${
              localSort === "asc"
                ? "on-solid bg-emerald-700 text-white"
                : "bg-white/6 text-white/70 hover:bg-white/10"
            }`}
          >
            <ArrowUp size={11} weight="bold" /> A → Z
          </button>
          <button
            data-testid={`sort-desc-${column}`}
            onClick={() => setLocalSort(localSort === "desc" ? null : "desc")}
            className={`flex-1 px-2 py-1.5 rounded text-[11px] font-mono uppercase tracking-widest font-bold transition flex items-center justify-center gap-1 ${
              localSort === "desc"
                ? "on-solid bg-emerald-700 text-white"
                : "bg-white/6 text-white/70 hover:bg-white/10"
            }`}
          >
            <ArrowDown size={11} weight="bold" /> Z → A
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-2">
        <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1">
          Filtrar
        </div>
        <div className="relative">
          <MagnifyingGlass size={11} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid={`filter-search-${column}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..."
            className="w-full bg-black/40 border border-white/10 rounded pl-6 pr-2 py-1.5 text-[11px] font-mono text-white outline-none focus:border-[#FACC15]/40"
          />
        </div>
      </div>

      {/* Value list */}
      <div className="flex items-center justify-between mb-1">
        <button
          data-testid={`filter-all-${column}`}
          onClick={selectAll}
          className="text-[9px] font-mono uppercase tracking-widest text-white/60 hover:text-white"
        >
          Todos
        </button>
        <button
          data-testid={`filter-none-${column}`}
          onClick={clearAll}
          className="text-[9px] font-mono uppercase tracking-widest text-white/40 hover:text-white"
        >
          Ninguno
        </button>
      </div>
      <div className="max-h-52 overflow-y-auto rounded border border-white/8 bg-black/20">
        {filtered.length === 0 ? (
          <div className="p-3 text-center text-[10px] font-mono text-white/30">
            Sin resultados
          </div>
        ) : (
          filtered.map((v) => {
            const s = String(v.value);
            const isChecked = !isNone && (allChecked || localSelected.has(s));
            return (
              <label
                key={s}
                data-testid={`filter-item-${column}-${s}`}
                className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-white/6"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggle(s)}
                  className="w-3.5 h-3.5 rounded accent-emerald-500 shrink-0"
                />
                <span className="text-[11px] font-mono text-white truncate">
                  {v.label ?? v.value}
                </span>
                {v.count != null && (
                  <span className="ml-auto text-[9px] font-mono text-white/40 tabular-nums">
                    {v.count}
                  </span>
                )}
              </label>
            );
          })
        )}
      </div>

      {/* Footer buttons */}
      <div className="mt-2 flex items-center justify-between gap-2">
        <button
          data-testid={`filter-clear-${column}`}
          onClick={clearFilter}
          className="text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-1"
        >
          <X size={10} weight="bold" /> Limpiar
        </button>
        <button
          data-testid={`filter-apply-${column}`}
          onClick={apply}
          className="on-solid px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-800 text-white font-heading font-bold text-[11px]"
        >
          Aplicar
        </button>
      </div>
    </div>
  );
}

/** Small funnel trigger to render next to the column label. */
export function ColumnFilterTrigger({ active, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`w-5 h-5 rounded flex items-center justify-center transition ${
        active
          ? "bg-[#FACC15]/25 text-[#FACC15]"
          : "text-white/30 hover:text-white/70 hover:bg-white/6"
      }`}
      title="Filtrar / Ordenar"
    >
      {active ? <Funnel size={11} weight="fill" /> : <FunnelSimple size={11} weight="bold" />}
    </button>
  );
}
